import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import RestHttpClient from '../../../../../common/RestHttpClient';
import { pushNotificationHandler } from '../../../../../common/PushNotification';
import {
  CustomerType,
  PrivateCustomerKsvResponse,
  CommercialCustomerKsvResponse,
} from '../../../../../generated/ApiClient';
import { KsvSearchFormValues } from './useKsvSearchForm';

export type KsvSearchResult = PrivateCustomerKsvResponse | CommercialCustomerKsvResponse;

export function useKsvSearch(
  customerType: CustomerType,
  customerId: number,
  form: UseFormReturn<KsvSearchFormValues>,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = ['identityCheck-ksv-search', customerType, customerId] as const;

  const runSearch = async (): Promise<KsvSearchResult[]> => {
    const values = form.getValues();
    if (customerType === CustomerType.Commercial) {
      const r = await RestHttpClient.searchCommercialCustomerKSV(
        values.companyName ?? '',
        values.uidNumber ?? undefined,
        values.commercialRegisterNumber ?? undefined,
        values.street,
        values.zipCode,
        values.city,
        undefined,
      );
      return r.data;
    }
    const r = await RestHttpClient.searchPrivateCustomerKSV(
      values.firstName ?? '',
      values.lastName ?? '',
      values.birthDate!,
      values.street,
      values.houseNumber,
      values.zipCode,
      values.city,
      undefined,
    );
    return r.data;
  };

  const { data: results = [], isFetching, isFetched, refetch } = useQuery<KsvSearchResult[]>({
    queryKey,
    queryFn: runSearch,
    enabled: false,
    cacheTime: 0,
  });

  const validate = (): boolean => {
    const values = form.getValues();
    const requiredFields =
      customerType === CustomerType.Commercial
        ? (['companyName'] as const)
        : (['firstName', 'lastName', 'birthDate'] as const);
    let valid = true;
    for (const field of requiredFields) {
      if (!values[field]) {
        form.setError(field as any, {
          type: 'required',
          message: t('customers.ksv.requiredForKsvSearch'),
        });
        valid = false;
      } else {
        form.clearErrors(field as any);
      }
    }
    return valid;
  };

  const search = async (): Promise<KsvSearchResult[]> => {
    if (!validate()) return [];
    const r = await refetch();
    const found = r.data ?? [];
    if (found.length === 0) {
      pushNotificationHandler.publish(t('customers.edit.ksvSearch.nothingFound'), 'warning');
    } else {
      pushNotificationHandler.publish(t('customers.edit.ksvSearch.found'), 'info');
    }
    return found;
  };

  const reset = () => {
    queryClient.removeQueries(queryKey);
  };

  return {
    search,
    results,
    isSearching: isFetching,
    hasSearched: isFetched,
    reset,
  };
}
